import { Action } from '@ngrx/store';

/** What the DevTools action history shows instead of a vendor secret. */
export const REDACTED = '[redacted]';

/**
 * Secret fields of the vendor connect bodies (`initiate{Abacus,Topal,Proffix,Accounto}Connect`):
 * Abacus `clientSecret`, Topal `password`, PROFFIX `restApiKey` + `password`, Accounto `apiKey`.
 */
const SECRET_BODY_FIELDS = ['clientSecret', 'password', 'restApiKey', 'apiKey'] as const;

/**
 * `actionSanitizer` for `StoreDevtoolsModule.instrument` (REV-21119, REV-22536 review).
 *
 * The DevTools instrumentation is active in production too — `logOnly` only turns off time travel and
 * dispatch, the Redux DevTools extension still records every action payload. The connect actions carry the
 * vendor secrets in `body`, so without this they stayed readable in the open tab's action history after
 * the modal closed. The Accounto key is worth the most of them: it has full write and delete rights on the
 * tenant (Accounto has no scopes). Returns a copy with those fields blanked; the dispatched action itself is
 * untouched, and any action without a secret field is passed through as the same object.
 */
export function redactConnectSecretsForDevtools<A extends Action>(action: A): A {
    const body = (action as { body?: unknown }).body;
    if (body == null || typeof body !== 'object') return action;

    const secretFields = SECRET_BODY_FIELDS.filter((field) => field in body);
    if (secretFields.length === 0) return action;

    const redactedBody: Record<string, unknown> = { ...(body as Record<string, unknown>) };
    for (const field of secretFields) redactedBody[field] = REDACTED;
    return { ...action, body: redactedBody };
}
